import { Link } from "react-router-dom";
import { Quote, Award, GraduationCap, Star, Calendar, ArrowUpRight } from "lucide-react";
import { IMG } from "../lib/assets";

const credentials = [
  { icon: GraduationCap, t: "Mahavastu Expert", d: "Certified Mahavastu Expert trained under the lineage of Dr. Khushdeep Bansal, founder of MahaVastu." },
  { icon: Award, t: "Vedic Astrology", d: "Formal study in Vedic Astrology, KP Astrology, Lal Kitab, Predictive Astrology and Gemstones." },
  { icon: Star, t: "Numerology, Rudraksha & Raaga Therapy", d: "Integrated remedial practices across various Vedic sciences." },
];

const testimonials = [
  {
    q: "Within three months of Divya's audit, our home felt lighter — and my husband's long-stuck business finally moved. No walls broken, just colour and placement.",
    a: "— Meenu S., Chandigarh",
  },
  {
    q: "I was sceptical. I'm not anymore. Her reading combined my chart and my office layout so precisely that it felt like she had read a diary I hadn't written.",
    a: "— Kamal K., Powai",
  },
  {
    q: "Elegant, grounded, and uncannily accurate. Tarini is not a trend — it is a practice.",
    a: "— Meera M., Powai",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32">
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="yantra-card overflow-hidden">
              <img
                src={IMG.consultantProfile}
                alt="Divya Agarwal"
                className="w-full h-[560px] object-cover"
                style={{ objectPosition: "30% 35%" }}
              />
            </div>
            <div className="mt-6 text-center">
              <div className="overline">Founder & Consultant</div>
              <div className="font-bodoni text-3xl text-[#1E1A17] mt-2">Divya Agarwal</div>
              <div className="text-sm text-[#6B5F55] mt-1">Mumbai · By appointment</div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="overline mb-4">About the Consultant</div>
            <h1 className="font-bodoni text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.05]">
              A quiet practice, a <span className="italic text-[#A67C00]">precise science</span>,
              a deeply personal craft.
            </h1>
            <p className="mt-8 text-[#6B5F55] leading-relaxed text-base">
              Divya Agarwal is a Mumbai-based Astro-Vastu consultant with over 5+ years of
              private practice serving families, entrepreneurs and creative professionals across
              India, Canada and Sydney. She reads space and stars as a single, coherent system —
              and prescribes remedies that fit seamlessly into real, lived-in homes.
              <span className="block mt-5">
                Using Vedic sciences, Divya offers simple solutions to align you with your stars and
                energise the life force within your living space — leading to the manifestation of
                your goals, happiness, health and wealth.
              </span>
              <span className="block mt-5 italic text-[#C8683F]">
                Simple solutions. Simple remedies. A simpler life.
              </span>
            </p>

            <div className="mt-10 yantra-divider w-full max-w-md" />

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-8 text-sm max-w-lg">
              <div>
                <div className="font-bodoni text-4xl text-[#A67C00]">5+</div>
                <div className="text-[#6B5F55] mt-1">Years of practice</div>
              </div>
              <div>
                <div className="font-bodoni text-4xl text-[#A67C00]">100+</div>
                <div className="text-[#6B5F55] mt-1">Private consultations</div>
              </div>
            </div>

            {/* Philosophy */}
            <div className="mt-16">
              <div className="overline mb-4">Philosophy</div>
              <p className="font-bodoni text-2xl md:text-3xl font-light leading-relaxed text-[#1E1A17]">
                “I don't prescribe rituals. I restore relationships — between a person, their stars,
                and the four walls they return to every night.”
              </p>
            </div>

            {/* Credentials */}
            <div className="mt-16">
              <div className="overline mb-6">Training & Credentials</div>
              <div className="space-y-4">
                {credentials.map((c, i) => (
                  <div key={i} data-testid={`credential-${i}`} className="flex items-start gap-5 b-gold p-5">
                    <c.icon className="text-[#A67C00] mt-1 shrink-0" size={20} />
                    <div>
                      <div className="font-bodoni text-lg text-[#1E1A17]">{c.t}</div>
                      <div className="text-sm text-[#6B5F55] mt-1">{c.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Specialisations */}
            <div className="mt-16">
              <div className="overline mb-6">Specialisations</div>
              <div className="flex flex-wrap gap-3">
                {[
                  "Residential Vastu", "Commercial Vastu", "Plot Selection",
                  "Entrance Analysis", "16-Zone Audit", "Vastu without Demolition",
                  "Vedic Birth Chart", "Gemstone & Yantra Therapy", "Career & Wealth Remedies",
                ].map((s) => (
                  <span key={s} className="text-xs uppercase tracking-[0.2em] text-[#C8683F] border border-[#C9A227]/30 px-4 py-2">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#F3EADB] border-y border-[#C9A227]/10 mt-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-4">Client Voices</div>
          <h2 className="font-bodoni text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-16 max-w-3xl">
            Private, particular, <span className="italic text-[#A67C00]">quietly transformative</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="yantra-card p-8 bg-white">
                <Quote className="text-[#A67C00]" size={22} />
                <p className="font-bodoni text-lg text-[#1E1A17] italic mt-5 leading-relaxed">
                  {t.q}
                </p>
                <div className="text-xs uppercase tracking-[0.25em] text-[#A67C00] mt-6">{t.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Calendar className="mx-auto text-[#A67C00]" size={32} />
          <h2 className="font-bodoni text-4xl sm:text-5xl font-light tracking-tight mt-8">
            Work directly with <span className="italic text-[#A67C00]">Divya</span>.
          </h2>
          <p className="mt-4 text-[#6B5F55]">
            Consultations are limited and by appointment only.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="mt-10 inline-flex items-center gap-2 bg-[#C9A227] text-white px-7 py-3.5 rounded-sm text-sm font-medium hover:bg-[#C8683F]"
          >
            Request an Appointment <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
